import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
// import { nthDerivative } from "@/lib/utils/math"; // or pass it in as a prop

function nthDerivative(arr, n = 0) {
  if (n <= 0) return arr;
  let cur = arr;
  for (let k = 0; k < n; k++) {
    cur = cur.slice(1).map((v, i) => v - cur[i]);
  }
  return cur;
}

export default function DerivLineChart({
  title = "",
  data = [1],
  width = 500,
  height = 400,
  marginTop = 20,
  marginRight = 20,
  marginBottom = 30,
  marginLeft = 60,
}) {
  const svgRef = useRef(null);
  const gxRef = useRef(null);
  const gyRef = useRef(null);

  const [dtn, setDtn] = useState(0);

  const newData = useMemo(() => nthDerivative(data, dtn), [data, dtn]);

  const x = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, Math.max(newData.length - 1, 1)])
        .range([marginLeft, width - marginRight]),
    [newData.length, marginLeft, width, marginRight]
  );

  const y = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain(d3.extent(newData.length ? newData : [0, 1]))
        .range([height - marginBottom, marginTop])
        .nice(),
    [newData, height, marginBottom, marginTop]
  );

  const line = useMemo(
    () => d3.line().x((_, i) => x(i)).y((d) => y(d)),
    [x, y]
  );

  const xticks = useMemo(() => x.ticks().slice(1), [x]);
  const yticks = useMemo(() => y.ticks().slice(1), [y]);

  useEffect(() => {
    if (gyRef.current) d3.select(gyRef.current).call(d3.axisLeft(y).ticks(6));
  }, [y]);

  useEffect(() => {
    if (gxRef.current)
      d3.select(gxRef.current).call(d3.axisBottom(x).ticks(8));
  }, [x]);

  // Tooltip state
  const [tooltip, setTooltip] = useState({
    html: "<p>Test</p>",
    left: 100,
    top: 200,
    show: false,
  });

  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const [xC, yC] = d3.pointer(e.nativeEvent, svgRef.current);
    setTooltip((t) => ({ ...t, left: xC, top: yC, show: true }));
  };

  const handleMouseLeave = () => {
    setTooltip((t) => ({ ...t, show: false }));
  };

  const derivText = (n) => {
    if (n === 0) return "Feature time series";
    const suffix = n <= 3 ? { 1: "st", 2: "nd", 3: "rd" }[n] : "th";
    return `${n}${suffix} order time derivative`;
  };

  return (
    <div className="plot-container" style={{ position: "relative" }}>
      {title !== "" && (
        <div className="head" style={{ display: "flex", flexDirection: "column" }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <div
            className="deriv-slider-row"
            style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
          >
            <label htmlFor="deriv-slider">Derivative:</label>
            <input
              type="range"
              id="deriv-slider"
              min={0}
              max={4}
              value={dtn}
              onChange={(e) => setDtn(Number(e.target.value))}
            />
            <span>{derivText(dtn)}</span>
          </div>
        </div>
      )}

      <div>
        <svg ref={svgRef} width={width} height={height}>
          {/* AXES */}
          <g
            ref={gxRef}
            className="axis"
            transform={`translate(0, ${height - marginBottom})`}
            style={{ fontSize: "0.9rem", fontWeight: 500 }}
          />
          <g
            ref={gyRef}
            className="axis"
            transform={`translate(${marginLeft}, 0)`}
            style={{  fontSize: "0.9rem", fontWeight: 500 }}
          />

          {/* GRID TICKS */}
          {xticks.map((d) => (
            <line
              key={`xtick-${d}`}
              className="tick"
              x1={x(d)}
              x2={x(d)}
              y1={marginTop}
              y2={height - marginBottom}
              stroke="#ccc"
            />
          ))}
          {yticks.map((d) => (
            <line
              key={`ytick-${d}`}
              className="tick"
              x1={marginLeft}
              x2={width - marginRight}
              y1={y(d)}
              y2={y(d)}
              stroke="#ccc"
            />
          ))}

          {/* LINE */}
          <path
            fill="none"
            stroke="steelblue"
            strokeWidth="1.5"
            d={newData.length ? line(newData) : ""}
          />

          {/* DOTS */}
          <g fill="white" stroke="steelblue" strokeWidth="1.5">
            {newData.map((d, i) => (
              <circle key={i} cx={x(i)} cy={y(d)} r="2.5" />
            ))}
          </g>

          {/* INTERACTION RECT */}
          {/* <rect
            width={width - marginLeft - marginRight}
            height={height - marginTop - marginBottom}
            x={marginLeft}
            y={marginTop}
            role="tooltip"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ pointerEvents: "all", fillOpacity: 0, strokeOpacity: 0 }}
          /> */}
        </svg>
      </div>

      {/* TOOLTIP */}
      <div
        className="tooltip"
        style={{
          position: "absolute",
          padding: 10,
          backgroundColor: "steelblue",
          color: "white",
          border: "1px solid white",
          borderRadius: 10,
          display: tooltip.show ? "block" : "none",
          opacity: 0.75,
          left: tooltip.left,
          top: tooltip.top,
          pointerEvents: "none",
        }}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: tooltip.html }}
      />
    </div>
  );
}
